import React, { useState, useEffect } from 'react';
import './BasketPage.css';
import ShoppingList from '../components/ShoppingList';
import PriceComparison from '../components/PriceComparison';

const BROWSE_PAGES = ['browse', 'aisle', 'subcategory', 'category'];

function BasketPage({
  basket,
  onRemove,
  onUpdateQuantity,
  onClear,
  onSave,
  onNavigateToProducts,
  savedBaskets = [],
  onLoadSavedBasket,
  onDeleteSavedBasket,
  onOpenPenny,
  fromPage,
  onBack,
}) {
  const showBackToBrowse = fromPage && BROWSE_PAGES.includes(fromPage) && onBack;
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('current');
  const [selectedSaved, setSelectedSaved] = useState(null); // saved basket detail view
  const [selectedProductForDetail, setSelectedProductForDetail] = useState(null);

  useEffect(() => {
    const loadStores = async () => {
      try {
        const { getStores } = await import('../utils/db');
        const storesData = await getStores();
        setStores(storesData);
      } catch (error) {
        console.error('Error loading stores:', error);
      } finally {
        setLoading(false);
      }
    };
    loadStores();
  }, []);

  const formatRelativeDate = (isoString) => {
    const diff = Date.now() - new Date(isoString).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    return `${days} days ago`;
  };

  const getCheapestPrice = (item) => {
    if (!item.prices?.length) return null;
    const valid = item.prices.filter(p => p.price > 0);
    if (!valid.length) return null;
    return Math.min(...valid.map(p => p.price));
  };

  const hasCurrentItems = basket.length > 0;
  const hasSavedBaskets = savedBaskets.length > 0;

  // Full empty state — nothing at all
  if (!hasCurrentItems && !hasSavedBaskets) {
    return (
      <div className="basket-page">
        <div className="basket-empty">
          <img
            src="/Penny Fox open eyes.png"
            alt="Penny"
            className="basket-empty-penny"
          />
          <p className="basket-empty-title">Your list is empty</p>
          <p className="basket-empty-sub">Add some products to get started</p>
          <button className="basket-browse-btn" onClick={onNavigateToProducts}>
            Browse products
          </button>
        </div>
      </div>
    );
  }

  // ── Product detail overlay (price comparison from basket item click) ── //
  if (selectedProductForDetail) {
    const detailPrices = (selectedProductForDetail.prices || [])
      .filter(p => p.price > 0)
      .map(p => {
        const normId = (p.storeId || '').replace(/^store_/, '');
        const store = stores.find(s => s.id === normId || s.id === p.storeId);
        return {
          storeId:   p.storeId,
          storeName: store?.name || normId.replace(/_/g, ' '),
          price:     p.price,
        };
      });
    return (
      <div className="basket-page">
        <PriceComparison
          product={selectedProductForDetail}
          prices={detailPrices}
          stores={stores}
          onBack={() => setSelectedProductForDetail(null)}
          onAddToBasket={null}
        />
      </div>
    );
  }

  return (
    <div className="basket-page">

      {/* ── Back to browse ── */}
      {showBackToBrowse && (
        <button className="basket-back-link" onClick={onBack}>
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="13 18 7 12 13 6"/>
          </svg>
          Back to browse
        </button>
      )}

      {/* ── Tab switcher ── */}
      <div className="basket-tabs">
        <button
          className={`basket-tab${activeTab === 'current' ? ' active' : ''}`}
          onClick={() => { setActiveTab('current'); setSelectedSaved(null); }}
        >
          My list
          {basket.length > 0 && (
            <span className="basket-tab-badge">{basket.length}</span>
          )}
        </button>
        <button
          className={`basket-tab${activeTab === 'saved' ? ' active' : ''}`}
          onClick={() => { setActiveTab('saved'); setSelectedSaved(null); }}
        >
          Saved lists
          {savedBaskets.length > 0 && (
            <span className="basket-tab-badge">{savedBaskets.length}</span>
          )}
        </button>
      </div>

      <div className="basket-container">

        {/* ── Current basket tab ── */}
        {activeTab === 'current' && (
          <>
            {loading && hasCurrentItems && (
              <div className="basket-loading">Loading…</div>
            )}

            {!loading && hasCurrentItems && (
              <ShoppingList
                items={basket}
                stores={stores}
                onRemove={onRemove}
                onUpdateQuantity={onUpdateQuantity}
                onSave={onSave}
                onClear={onClear}
                onOpenPenny={onOpenPenny}
                onSelectProduct={setSelectedProductForDetail}
              />
            )}

            {!hasCurrentItems && (
              <div className="basket-tab-empty">
                <img
                  src="/Penny Fox open eyes.png"
                  alt="Penny"
                  className="basket-tab-empty-img"
                />
                <p className="basket-tab-empty-title">Nothing here yet</p>
                <p className="basket-tab-empty-sub">
                  Browse products or load a saved list
                </p>
                <div className="basket-tab-empty-actions">
                  <button className="basket-browse-btn" onClick={onNavigateToProducts}>
                    Browse products
                  </button>
                  {hasSavedBaskets && (
                    <button
                      className="basket-load-link"
                      onClick={() => setActiveTab('saved')}
                    >
                      View saved lists
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Saved baskets tab ── */}
        {activeTab === 'saved' && !selectedSaved && (
          <>
            {hasSavedBaskets ? (
              <div className="saved-baskets">
                {savedBaskets.map(saved => (
                  <div
                    key={saved.id}
                    className="saved-basket-row"
                    onClick={() => setSelectedSaved(saved)}
                  >
                    <div className="saved-basket-icon">🛒</div>
                    <div className="saved-basket-info">
                      <div className="saved-basket-name">{saved.name}</div>
                      <div className="saved-basket-meta">
                        {saved.items.length} item{saved.items.length !== 1 ? 's' : ''} · Last used {formatRelativeDate(saved.lastUsedAt)}
                      </div>
                    </div>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 3l5 5-5 5"/>
                    </svg>
                  </div>
                ))}
              </div>
            ) : (
              <div className="basket-tab-empty">
                <p className="basket-tab-empty-title">No saved lists</p>
                <p className="basket-tab-empty-sub">
                  Fill your list and tap "Save list" to store it here
                </p>
                <button
                  className="basket-browse-btn"
                  onClick={() => setActiveTab('current')}
                >
                  Go to my list
                </button>
              </div>
            )}
          </>
        )}

        {/* ── Saved basket detail view ── */}
        {activeTab === 'saved' && selectedSaved && (
          <div className="saved-detail">

            {/* Back to list */}
            <button
              className="saved-detail-back"
              onClick={() => setSelectedSaved(null)}
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="13 18 7 12 13 6"/>
              </svg>
              Saved lists
            </button>

            {/* Basket title + meta */}
            <div className="saved-detail-header">
              <h2 className="saved-detail-name">{selectedSaved.name}</h2>
              <p className="saved-detail-meta">
                {selectedSaved.items.length} item{selectedSaved.items.length !== 1 ? 's' : ''} · Saved {formatRelativeDate(selectedSaved.savedAt)}
              </p>
            </div>

            {/* Items list */}
            <div className="saved-detail-items">
              <div className="basket-section-header">
                <span>Items</span>
                <span className="basket-item-count">{selectedSaved.items.length}</span>
              </div>
              {selectedSaved.items.map((item, i) => {
                const cheapest = getCheapestPrice(item);
                return (
                  <div key={item.id || i} className="saved-detail-row">
                    <div className="saved-detail-row-info">
                      <span className="saved-detail-row-name">{item.name}</span>
                      {item.size && (
                        <span className="saved-detail-row-meta">{item.size}</span>
                      )}
                    </div>
                    <div className="saved-detail-row-right">
                      <span className="saved-detail-qty">×{item.quantity}</span>
                      <span className="saved-detail-price">
                        {cheapest
                          ? `From £${(cheapest * item.quantity).toFixed(2)}`
                          : '—'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Actions */}
            <div className="saved-detail-actions">
              {hasCurrentItems && (
                <p className="saved-detail-warning">
                  You have {basket.length} item{basket.length !== 1 ? 's' : ''} in your current list
                </p>
              )}
              <button
                className="basket-save-btn"
                onClick={() => {
                  onLoadSavedBasket('replace', selectedSaved);
                  setSelectedSaved(null);
                  setActiveTab('current');
                }}
              >
                {hasCurrentItems ? 'Replace current list' : 'Load list'}
              </button>
              {hasCurrentItems && (
                <button
                  className="load-secondary-btn"
                  onClick={() => {
                    onLoadSavedBasket('add', selectedSaved);
                    setSelectedSaved(null);
                    setActiveTab('current');
                  }}
                >
                  Add to current list
                </button>
              )}
              <button
                className="load-delete-btn"
                onClick={() => {
                  onDeleteSavedBasket(selectedSaved.id);
                  setSelectedSaved(null);
                }}
              >
                Delete this list
              </button>
            </div>

          </div>
        )}

      </div>
    </div>
  );
}

export default BasketPage;
